import moment from 'moment';
import { userRoles } from '../constants/strings';
import { keys } from './translator/translation_keys';
import { pdfjs } from 'react-pdf';
import defaultLogo from '../assets/workspace_image.png';

export const isProd = !(
  !process.env.NODE_ENV || process.env.NODE_ENV === 'development'
);

// WARNING:  This is order dependant on keys
// Ex. {a:1,b:2} != {b:2,a:1}
export const deepCompareObjects = (a, b) =>
  JSON.stringify(a) === JSON.stringify(b);

export const isLoggedIn = () => !!localStorage.getItem('token');

export const inputTypeToIconMap = {
  DISTANCE: 'straighten',
  IMAGE: 'photo_camera',
  NUMBER: '123',
  TEST: 'check_circle',
  TEXT: 'text_fields',
  VOLUME: 'oil_barrel',
  PERCENT: 'percent',
  PRESSURE: 'compress',
  SELECT_SINGLE: 'check_box',
  SELECT_MULTI: 'library_add_check',
  USER: 'account_box',
  TABLE: 'table_view',
  SIGNATURE_SINGLE: 'border_color',
  SIGNATURE_MULTI: 'rate_review',
  DATE: 'calendar_month',
  MATRIX: 'apps',
  TEXT_MULTI: 'rtt',
  FILE_SINGLE: 'upload_file',
  FILE_MULTI: 'drive_folder_upload',
  IMAGE_MULTI: 'add_a_photo',
  USER_MULTI: 'group',
  TIME: 'access_time',
  DATE_TIME: 'event',
  AUDIO: 'mic',
  ASSET: 'local_shipping',
  DROPDOWN_SINGLE: 'arrow_drop_down_circle',
  DROPDOWN_MULTI: 'pending',
  SELECT_CHECKLIST: 'checklist',
  GPS: 'add_location_alt',
  MESSAGE: 'message',
  RISK_REGISTRY: 'health_and_safety',
};

export const observationSupportedTypes = [
  'DATE',
  'DATE_TIME',
  'TIME',
  'SELECT_SINGLE',
  'SELECT_MULTI',
  'TEXT',
  'TEXT_MULTI',
];
export function getObservationIcon(type) {
  return inputTypeToIconMap[
    observationSupportedTypes.includes(type) ? type : 'TEXT'
  ];
}

export const getQuestionType = (inputType) => {
  switch (inputType) {
    case 'SELECT_SINGLE':
    case 'SELECT_MULTI':
      return 'Select';
    case 'DATE_TIME':
    case 'DATE':
    case 'TIME':
      return 'Date';
    default:
      return 'Text';
  }
};

export const percentageToColor = (percentage) => {
  if (percentage >= 80) {
    return 'green';
  } else if (percentage >= 50) {
    return 'yellow';
  }
  return 'red';
};

export const hasWebpSupport = () => {
  return false;
  // var elem = document.createElement('canvas');

  // if (!!(elem.getContext && elem.getContext('2d'))) {
  //   // was able or not to get WebP representation
  //   return (
  //     elem.toDataURL('image/webp').indexOf('data:image/webp') === 0
  //   );
  // } else {
  //   // very old browser like IE 8, canvas not supported
  //   return false;
  // }
};

export const cleanIndexedDB = (data) => {
  return JSON.parse(JSON.stringify(data));
};

export const isAdmin = (user) => user?.role === userRoles.admin;

export const getFileSizeString = (size) => {
  if (size < 1024) {
    return `${size} B`;
  } else if (size < 1024 * 1024) {
    return `${Math.round(size / 1024)} KB`;
  } else if (size < 1024 * 1024 * 1024) {
    return `${Math.round(size / 1024 / 1024)} MB`;
  }
};

export const clamp = (num, min, max) => Math.min(Math.max(num, min), max);

export const backgroundScrollLock = (boolean) => {
  if (boolean) {
    document.body.style.position = 'fixed';
  } else {
    document.body.style.position = 'unset';
  }
};

export const workspacePhaseOptions = [
  'Lead',
  'Bid',
  'Scheduled',
  'In Progress',
  'Completed',
  'Cancelled',
];

export const workspaceStatusOptions = ['ACTIVE', 'INACTIVE'];

export const workspaceInputOptions = [
  { title: keys.workspace.JOB_ID, query: 'jobId' },
  { title: keys.workspace.INTERNAL_JOB_ID, query: 'internalJobId' },
  { title: keys.workspace.JOB_CODE, query: 'jobCode' },
  { title: keys.workspace.PROJECT_VALUE, query: 'value' },
];

export const workspaceDateSelectors = [
  { title: keys.workspace.START_DATE, query: 'scheduledStartDate' },
  { title: keys.workspace.END_DATE, query: 'scheduledEndDate' },
];

export const API_DELAY = 750;

export const b64toBlob = (b64Data, contentType = '', sliceSize = 512) => {
  const byteCharacters = atob(b64Data);
  const byteArrays = [];

  for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
    const slice = byteCharacters.slice(offset, offset + sliceSize);

    const byteNumbers = new Array(slice.length);
    for (let i = 0; i < slice.length; i++) {
      byteNumbers[i] = slice.charCodeAt(i);
    }

    const byteArray = new Uint8Array(byteNumbers);
    byteArrays.push(byteArray);
  }

  const blob = new Blob(byteArrays, { type: contentType });
  return blob;
};

export const setTimeStamp = () => {
  localStorage.setItem('modalTimestamp', Date.now().toString());
  return true;
};

export const networkModalLastOpened = () => {
  return localStorage.getItem('modalTimestamp');
};

export const timeStampDiff = (timeStamp) => {
  return moment().diff(moment(parseInt(timeStamp, 10)), 'hours');
};

export const isNetworkModalStale = () => {
  const timeStamp = networkModalLastOpened();
  if (timeStamp) {
    if (timeStampDiff(timeStamp) > 24) {
      setTimeStamp();
      return true;
    } else if (timeStampDiff(timeStamp) <= 24) {
      return false;
    }
  } else if (!timeStamp) {
    return setTimeStamp();
  }
};

export const convertPDFToImages = async (pdfUrl) => {
  try {
    const loadingTask = pdfjs.getDocument({ url: pdfUrl });
    const pdf = await loadingTask.promise;
    const page = await pdf.getPage(1);
    const viewport = page.getViewport({ scale: 1 }); // Adjust scale as needed
    const canvas = document.createElement('canvas');
    const canvasContext = canvas.getContext('2d');
    canvas.height = viewport.height;
    canvas.width = viewport.width;
    const renderContext = {
      canvasContext,
      viewport,
    };
    await page.render(renderContext).promise;
    const imageUrl = canvas.toDataURL('image/png', 0.5); // Adjust quality as needed
    return imageUrl;
  } catch (error) {
    console.error('Error converting PDF to image:', error);
    return null;
  }
};

export const customColors = {
  ORANGE: '#fd7e14',
  BLUE: '#0d6efd',
  PINK: '#d63384',
  RED_LIGHT: '#e35d6a',
  GREEN_LIGHT: '#479f76',
  YELLOW: '#ffc107',
  GREEN: '#28a745',
  RED: '#e9222c',
  PURPLE_LIGHT: '#8c68cd',
  YELLOW_LIGHT: '#f1c84b',
  SECONDARY: '#6e6e73',
  SECONDARY_LIGHT: '#ced4da',
  DARK_GRAY: '#54595e',
  DARK_THEME_DARK: '#1e1e1e',
  DARK_THEME_MEDIUM: '#2f2f2f',
  DARK_THEME_LIGHT: '#c0c0c0',
  BLUE_LIGHT: '#6ea8fe',
  DARKBLUE: '#0a58ca',
  PURPLE: '#6610f2',
  PURPLELIGHT: '#8c68cd',
  DARKRED: '#b02a37',
  GREEN_ULTRA_LIGHT: '#6abe97',
  DARK_GRAY_TRANSLUCENT: '#54595e55',
  OFF_WHITE: '#fafafd',
};

const iconArray = [
  'directions_car',
  'local_shipping',
  'home',
  'construction',
  'plumbing',
  'two_wheeler',
  'home_repair_service',
  'favorite',
  'person',
  'check',
  'close',
  'hardware',
  'carpenter',
  'recommend',
  'star',
  'local_gas_station',
  'alarm',
  'account_circle',
  'add',
  'apps',
  'build',
  'call',
  'camera',
  'chat',
  'cloud',
  'code',
  'comment',
  'delete',
  'edit',
  'email',
  'face',
  'file_copy',
  'filter_list',
  'fitness_center',
  'folder',
  'help',
  'info',
  'language',
  'map',
  'notifications',
  'palette',
  'print',
  'save',
  'security',
  'shopping_cart',
  'thumb_up',
  'visibility',
  'directions_run',
  'local_pizza',
  'announcement',
  'campaign',
  'flight',
  'flight_land',
  'bolt',
  'lightbulb',
  'airplane_mode',
  'light_mode',
  'psychology',
  'psychology_alt',
  'follow_the_signs',
  'pool',
  'attach_file',
  'assistant_navigation',
  'yard',
  'lunch_dining',
  'fire_extinguisher',
  'fire_truck',
  'local_fire_department',
  'pedal_bike',
  'directions_boat',
  'snowmobile',
  'sanitizer',
  'rocket_launch',
  'search',

  // --- added: lots more common Material icon names ---
  'accessibility',
  'accessibility_new',
  'accessible',
  'account_balance',
  'account_balance_wallet',
  'account_box',
  'account_tree',
  'add_a_photo',
  'add_alarm',
  'add_box',
  'add_circle',
  'add_circle_outline',
  'add_location',
  'add_location_alt',
  'add_photo_alternate',
  'add_road',
  'add_task',
  'adjust',
  'admin_panel_settings',
  'ads_click',
  'agriculture',
  'air',
  'airline_seat_recline_extra',
  'airline_seat_recline_normal',
  'airplanemode_active',
  'airplanemode_inactive',
  'airport_shuttle',
  'alarm_add',
  'alarm_off',
  'alarm_on',
  'all_inbox',
  'all_inclusive',
  'alternate_email',
  'analytics',
  'anchor',
  'android',
  'apartment',
  'api',
  'app_registration',
  'app_settings_alt',
  'architecture',
  'archive',
  'arrow_back',
  'arrow_back_ios',
  'arrow_back_ios_new',
  'arrow_circle_down',
  'arrow_circle_left',
  'arrow_circle_right',
  'arrow_circle_up',
  'arrow_downward',
  'arrow_drop_down',
  'arrow_drop_up',
  'arrow_forward',
  'arrow_forward_ios',
  'arrow_left',
  'arrow_right',
  'arrow_upward',
  'article',
  'aspect_ratio',
  'assessment',
  'assignment',
  'assignment_ind',
  'assignment_late',
  'assignment_return',
  'assignment_returned',
  'assignment_turned_in',
  'assistant',
  'assistant_photo',
  'attach_email',
  'attach_money',
  'attractions',
  'audiotrack',
  'auto_awesome',
  'auto_fix_high',
  'auto_fix_normal',
  'auto_fix_off',
  'autorenew',
  'av_timer',
  'baby_changing_station',
  'back_hand',
  'backpack',
  'backup',
  'backup_table',
  'badge',
  'bakery_dining',
  'balance',
  'balcony',
  'ballot',
  'bar_chart',
  'batch_prediction',
  'bathroom',
  'bathtub',
  'battery_alert',
  'battery_charging_full',
  'battery_full',
  'battery_saver',
  'battery_std',
  'beach_access',
  'bed',
  'bedroom_baby',
  'bedroom_child',
  'bedroom_parent',
  'bedtime',
  'beenhere',
  'bento',
  'bike_scooter',
  'biotech',
  'blender',
  'blind',
  'blinds',
  'block',
  'bloodtype',
  'bluetooth',
  'bluetooth_audio',
  'bluetooth_connected',
  'bluetooth_disabled',
  'bluetooth_searching',
  'blur_on',
  'book',
  'bookmark',
  'bookmark_add',
  'bookmark_added',
  'bookmark_border',
  'bookmark_remove',
  'bookmarks',
  'border_all',
  'border_color',
  'border_horizontal',
  'border_inner',
  'border_left',
  'border_outer',
  'border_right',
  'border_style',
  'border_top',
  'border_vertical',
  'boy',
  'branding_watermark',
  'breakfast_dining',
  'brightness_4',
  'brightness_5',
  'brightness_6',
  'broken_image',
  'browser_not_supported',
  'brunch_dining',
  'brush',
  'bubble_chart',
  'bug_report',
  'build_circle',
  'bungalow',
  'burst_mode',
  'bus_alert',
  'business',
  'business_center',
  'cabin',
  'cable',
  'cached',
  'cake',
  'calculate',
  'calendar_month',
  'calendar_today',
  'calendar_view_day',
  'calendar_view_month',
  'calendar_view_week',
  'call_end',
  'call_made',
  'call_merge',
  'call_missed',
  'call_received',
  'call_split',
  'call_to_action',
  'camera_alt',
  'camera_front',
  'camera_rear',
  'camera_roll',
  'cancel',
  'cancel_presentation',
  'car_rental',
  'car_repair',
  'card_giftcard',
  'card_membership',
  'card_travel',
  'carpenter',
  'cases',
  'casino',
  'cast',
  'cast_connected',
  'catching_pokemon',
  'category',
  'celebration',
  'cell_tower',
  'center_focus_strong',
  'center_focus_weak',
  'chair',
  'chair_alt',
  'change_circle',
  'change_history',
  'charging_station',
  'check_box',
  'check_box_outline_blank',
  'check_circle',
  'check_circle_outline',
  'chevron_left',
  'chevron_right',
  'child_care',
  'child_friendly',
  'chrome_reader_mode',
  'circle',
  'clean_hands',
  'cleaning_services',
  'clear_all',
  'close_fullscreen',
  'closed_caption',
  'cloud_done',
  'cloud_download',
  'cloud_off',
  'cloud_queue',
  'cloud_upload',
  'co_present',
  'coffee',
  'coffee_maker',
  'color_lens',
  'colorize',
  'comment_bank',
  'commute',
  'compare',
  'compare_arrows',
  'compass_calibration',
  'compress',
  'computer',
  'confirmation_number',
  'connect_without_contact',
  'connected_tv',
  'construction',
  'contact_mail',
  'contact_page',
  'contact_phone',
  'contacts',
  'content_copy',
  'content_cut',
  'content_paste',
  'control_camera',
  'copy_all',
  'copyright',
  'coronavirus',
  'corporate_fare',
  'create',
  'create_new_folder',
  'credit_card',
  'credit_card_off',
  'cruelty_free',
  'css',
  'currency_exchange',
  'currency_rupee',
  'dark_mode',
  'dashboard',
  'data_exploration',
  'data_saver_off',
  'data_saver_on',
  'data_usage',
  'date_range',
  'deck',
  'dehaze',
  'delete_forever',
  'delete_outline',
  'density_large',
  'density_medium',
  'density_small',
  'description',
  'desktop_access_disabled',
  'desktop_mac',
  'desktop_windows',
  'details',
  'developer_board',
  'developer_mode',
  'device_hub',
  'devices',
  'devices_other',
  'dialpad',
  'diamond',
  'difference',
  'dining',
  'dinner_dining',
  'directions',
  'directions_bike',
  'directions_bus',
  'directions_subway',
  'directions_transit',
  'directions_walk',
  'disc_full',
  'dns',
  'do_not_disturb',
  'do_not_disturb_alt',
  'do_not_disturb_on',
  'document_scanner',
  'domain',
  'domain_add',
  'domain_verification',
  'done',
  'done_all',
  'done_outline',
  'download',
  'download_done',
  'download_for_offline',
  'downloading',
  'drafts',
  'drag_handle',
  'drive_eta',
  'drive_file_move',
  'drive_file_rename_outline',
  'drive_folder_upload',
  'dry',
  'dry_cleaning',
  'duo',
  'dynamic_form',
  'eco',
  'edit_location',
  'edit_location_alt',
  'eject',
  'electric_bike',
  'electric_car',
  'electric_moped',
  'electric_rickshaw',
  'electric_scooter',
  'electrical_services',
  'elevator',
  'email',
  'emergency',
  'emergency_share',
  'engineering',
  'equalizer',
  'error',
  'error_outline',
  'escalator',
  'escalator_warning',
  'event',
  'event_available',
  'event_busy',
  'event_note',
  'expand',
  'expand_circle_down',
  'expand_less',
  'expand_more',
  'explore',
  'explore_off',
  'extension',
  'extension_off',
  'face_retouching_natural',
  'fact_check',
  'family_restroom',
  'fast_forward',
  'fast_rewind',
  'fastfood',
  'favorite_border',
  'fax',
  'feedback',
  'female',
  'festival',
  'file_download',
  'file_download_done',
  'file_present',
  'file_upload',
  'filter_alt',
  'filter_alt_off',
  'find_in_page',
  'find_replace',
  'fingerprint',
  'fireplace',
  'first_page',
  'flag',
  'flare',
  'flash_off',
  'flash_on',
  'flashlight_off',
  'flashlight_on',
  'flight_takeoff',
  'flip',
  'flip_camera_android',
  'flip_camera_ios',
  'flip_to_back',
  'flip_to_front',
  'folder_open',
  'folder_shared',
  'font_download',
  'format_align_center',
  'format_align_justify',
  'format_align_left',
  'format_align_right',
  'format_bold',
  'format_clear',
  'format_color_fill',
  'format_color_reset',
  'format_color_text',
  'format_indent_decrease',
  'format_indent_increase',
  'format_italic',
  'format_line_spacing',
  'format_list_bulleted',
  'format_list_numbered',
  'format_paint',
  'format_quote',
  'format_shapes',
  'format_size',
  'format_strikethrough',
  'format_underlined',
  'forum',
  'forward',
  'forward_to_inbox',
  'fullscreen',
  'fullscreen_exit',
  'functions',
  'g_translate',
  'gamepad',
  'games',
  'garage',
  'gas_meter',
  'gavel',
  'gesture',
  'get_app',
  'gif',
  'gif_box',
  'girl',
  'gite',
  'gps_fixed',
  'gps_not_fixed',
  'gps_off',
  'grade',
  'grading',
  'grid_3x3',
  'grid_4x4',
  'grid_goldenratio',
  'grid_on',
  'grid_view',
  'group',
  'group_add',
  'group_work',
  'handshake',
  'handyman',
  'headphones',
  'headphones_battery',
  'headset',
  'headset_mic',
  'health_and_safety',
  'hearing',
  'hearing_disabled',
  'height',
  'help_center',
  'hide_image',
  'history',
  'history_edu',
  'holiday_village',
  'home_max',
  'home_mini',
  'home_work',
  'horizontal_rule',
  'hotel',
  'hourglass_bottom',
  'hourglass_empty',
  'hourglass_full',
  'hourglass_top',
  'house',
  'house_siding',
  'how_to_reg',
  'how_to_vote',
  'https',
  'image',
  'image_aspect_ratio',
  'image_not_supported',
  'image_search',
  'imagesearch_roller',
  'import_contacts',
  'import_export',
  'inbox',
  'indeterminate_check_box',
  'input',
  'insert_chart',
  'insert_comment',
  'insert_drive_file',
  'insert_emoticon',
  'insert_invitation',
  'inventory',
  'inventory_2',
  'ios_share',
  'key',
  'keyboard',
  'keyboard_alt',
  'keyboard_arrow_down',
  'keyboard_arrow_left',
  'keyboard_arrow_right',
  'keyboard_arrow_up',
  'keyboard_backspace',
  'keyboard_capslock',
  'keyboard_hide',
  'keyboard_return',
  'keyboard_tab',
  'keyboard_voice',
  'king_bed',
  'kitchen',
  'label',
  'label_important',
  'label_off',
  'landscape',
  'language',
  'laptop',
  'laptop_chromebook',
  'laptop_mac',
  'laptop_windows',
  'last_page',
  'launch',
  'layers',
  'layers_clear',
  'leaderboard',
  'leak_add',
  'leak_remove',
  'legend_toggle',
  'library_add',
  'library_books',
  'library_music',
  'light_mode',
  'link',
  'link_off',
  'list',
  'live_help',
  'live_tv',
  'local_airport',
  'local_atm',
  'local_bar',
  'local_cafe',
  'local_dining',
  'local_drink',
  'local_hospital',
  'local_hotel',
  'local_library',
  'local_mall',
  'local_offer',
  'local_parking',
  'local_pharmacy',
  'local_phone',
  'local_post_office',
  'local_taxi',
  'location_city',
  'location_disabled',
  'location_off',
  'location_on',
  'location_searching',
  'lock',
  'lock_clock',
  'lock_open',
  'login',
  'logout',
  'looks',
  'loop',
  'mail',
  'mail_outline',
  'manage_accounts',
  'manage_search',
  'map',
  'mark_chat_read',
  'mark_chat_unread',
  'mark_email_read',
  'mark_email_unread',
  'masks',
  'mediation',
  'medical_services',
  'meeting_room',
  'memory',
  'menu',
  'menu_book',
  'menu_open',
  'merge',
  'mic',
  'mic_none',
  'mic_off',
  'military_tech',
  'minimize',
  'miscellaneous_services',
  'mode',
  'mode_edit',
  'money',
  'money_off',
  'money_off_csred',
  'monitor',
  'monitor_heart',
  'mood',
  'mood_bad',
  'more_horiz',
  'more_time',
  'more_vert',
  'motion_photos_on',
  'motorcycle',
  'mouse',
  'move_to_inbox',
  'movie',
  'movie_creation',
  'music_note',
  'music_off',
  'my_location',
  'navigation',
  'near_me',
  'near_me_disabled',
  'new_releases',
  'next_plan',
  'next_week',
  'nightlight',
  'nightlife',
  'nights_stay',
  'no_accounts',
  'no_backpack',
  'no_cell',
  'no_drinks',
  'no_encryption',
  'no_flash',
  'no_food',
  'no_luggage',
  'no_meeting_room',
  'no_photography',
  'no_sim',
  'no_stroller',
  'no_transfer',
  'north',
  'north_east',
  'north_west',
  'note',
  'note_add',
  'notes',
  'notification_add',
  'notifications_active',
  'notifications_none',
  'notifications_off',
  'notifications_paused',
  'offline_bolt',
  'offline_pin',
  'open_in_browser',
  'open_in_full',
  'open_in_new',
  'open_with',
  'outbound',
  'outbox',
  'outlet',
  'paid',
  'palette',
  'pan_tool',
  'panorama',
  'park',
  'party_mode',
  'pause',
  'pause_circle',
  'pause_circle_filled',
  'pause_circle_outline',
  'payment',
  'payments',
  'people',
  'people_alt',
  'people_outline',
  'percent',
  'perm_identity',
  'person_add',
  'person_add_alt',
  'person_off',
  'person_outline',
  'phone',
  'phone_android',
  'phone_disabled',
  'phone_iphone',
  'phonelink',
  'phonelink_off',
  'photo',
  'photo_camera',
  'photo_library',
  'picture_as_pdf',
  'pie_chart',
  'pin',
  'pin_drop',
  'place',
  'plagiarism',
  'play_arrow',
  'play_circle',
  'play_circle_filled',
  'play_circle_outline',
  'playlist_add',
  'playlist_add_check',
  'playlist_play',
  'plumbing',
  'podcasts',
  'policy',
  'power',
  'power_off',
  'power_settings_new',
  'precision_manufacturing',
  'preview',
  'price_change',
  'price_check',
  'print_disabled',
  'priority_high',
  'privacy_tip',
  'psychology',
  'public',
  'publish',
  'qr_code',
  'qr_code_2',
  'query_builder',
  'question_answer',
  'quickreply',
  'quiz',
  'radar',
  'radio',
  'radio_button_checked',
  'radio_button_unchecked',
  'railway_alert',
  'ramen_dining',
  'rate_review',
  'receipt',
  'receipt_long',
  'redo',
  'refresh',
  'remove',
  'remove_circle',
  'remove_circle_outline',
  'reorder',
  'repeat',
  'repeat_one',
  'reply',
  'reply_all',
  'report',
  'report_gmailerrorred',
  'report_off',
  'report_problem',
  'request_quote',
  'restaurant',
  'restaurant_menu',
  'restore',
  'restore_from_trash',
  'restore_page',
  'route',
  'router',
  'rss_feed',
  'rule',
  'run_circle',
  'safety_divider',
  'sailing',
  'satellite',
  'schedule',
  'school',
  'science',
  'score',
  'screen_search_desktop',
  'screen_share',
  'screenshot',
  'sd_card',
  'sd_storage',
  'search_off',
  'security_update_good',
  'security_update_warning',
  'send',
  'send_and_archive',
  'send_to_mobile',
  'settings',
  'settings_accessibility',
  'settings_applications',
  'settings_backup_restore',
  'settings_bluetooth',
  'settings_brightness',
  'settings_cell',
  'settings_ethernet',
  'settings_input_antenna',
  'settings_input_component',
  'settings_input_hdmi',
  'settings_input_svideo',
  'settings_overscan',
  'settings_phone',
  'settings_power',
  'settings_remote',
  'settings_suggest',
  'settings_system_daydream',
  'settings_voice',
  'share',
  'shield',
  'shield_moon',
  'shopping_bag',
  'shopping_basket',
  'shop',
  'shop_2',
  'shop_two',
  'shortcut',
  'shower',
  'shuffle',
  'signal_cellular_alt',
  'signal_wifi_4_bar',
  'sim_card',
  'single_bed',
  'skateboarding',
  'skip_next',
  'skip_previous',
  'smart_button',
  'smart_display',
  'smartphone',
  'smoke_free',
  'smoking_rooms',
  'snooze',
  'sort',
  'south',
  'south_east',
  'south_west',
  'spa',
  'space_bar',
  'speaker',
  'speaker_notes',
  'speaker_notes_off',
  'speed',
  'spellcheck',
  'sports',
  'sports_basketball',
  'sports_esports',
  'sports_football',
  'sports_golf',
  'sports_hockey',
  'sports_mma',
  'sports_motorsports',
  'sports_soccer',
  'sports_tennis',
  'square_foot',
  'stacked_bar_chart',
  'stairs',
  'star_border',
  'star_border_purple500',
  'star_half',
  'sticky_note_2',
  'stop',
  'stop_circle',
  'storage',
  'store',
  'storefront',
  'straighten',
  'streetview',
  'strikethrough_s',
  'stroller',
  'style',
  'subdirectory_arrow_left',
  'subdirectory_arrow_right',
  'subject',
  'subscriptions',
  'subway',
  'summarize',
  'support',
  'support_agent',
  'swap_calls',
  'swap_horiz',
  'swap_vert',
  'swap_vertical_circle',
  'sync',
  'sync_alt',
  'sync_disabled',
  'system_update',
  'tab',
  'table_chart',
  'tag',
  'tag_faces',
  'takeout_dining',
  'tapas',
  'task',
  'task_alt',
  'taxi_alert',
  'terrain',
  'text_fields',
  'text_format',
  'theaters',
  'thumb_down',
  'thumb_down_alt',
  'thumb_up_alt',
  'timeline',
  'timer',
  'tips_and_updates',
  'title',
  'today',
  'toggle_off',
  'toggle_on',
  'toll',
  'topic',
  'touch_app',
  'tour',
  'traffic',
  'train',
  'tram',
  'transfer_within_a_station',
  'transform',
  'translate',
  'trending_down',
  'trending_flat',
  'trending_up',
  'tsunami',
  'tune',
  'turn_left',
  'turn_right',
  'tv',
  'undo',
  'unfold_less',
  'unfold_more',
  'unpublished',
  'update',
  'upgrade',
  'upload',
  'upload_file',
  'verified',
  'verified_user',
  'vertical_align_bottom',
  'vertical_align_center',
  'vertical_align_top',
  'video_call',
  'video_camera_back',
  'video_camera_front',
  'videocam',
  'videocam_off',
  'view_agenda',
  'view_array',
  'view_carousel',
  'view_column',
  'view_comfy',
  'view_day',
  'view_headline',
  'view_in_ar',
  'view_list',
  'view_module',
  'view_quilt',
  'view_sidebar',
  'view_stream',
  'view_week',
  'visibility_off',
  'volume_down',
  'volume_mute',
  'volume_off',
  'volume_up',
  'vpn_key',
  'vpn_lock',
  'warning',
  'wash',
  'watch',
  'water',
  'water_damage',
  'wb_cloudy',
  'wb_incandescent',
  'wb_iridescent',
  'wb_shade',
  'wb_sunny',
  'wb_twilight',
  'weekend',
  'whatshot',
  'wifi',
  'wifi_off',
  'work',
  'work_outline',
  'workspace_premium',
  'wrap_text',
  'yard',
  'zoom_in',
  'zoom_out',
];
export const iconNames = [...new Set(iconArray)];

export { multiLineTruncate } from '@opas-mobile/report-export';

export function sanitizeArray(array) {
  if (!Array.isArray) {
    return array;
  }
  const sanitize = array.filter((v) => v.trim() !== '');
  return sanitize.join('|');
}

export function sortItemsByDateModified(array) {
  return array.sort((a, b) => {
    const dateA = moment(a.dateModified);
    const dateB = moment(b.dateModified);

    if (dateA.isBefore(dateB)) {
      return -1;
    }
    if (dateA.isAfter(dateB)) {
      return 1;
    }
    return 0;
  });
}

export const defaultUserAvatar =
  'https://app.opasmobile.com/assets/graphics/default-user-avatar.webp';

export const defaultWorkspaceLogo = defaultLogo;

export const areWeTestingWithCypress = () =>
  process.env.REACT_APP_IS_TEST_ENV === 'true';

export const actions = {
  create: 'CREATE',
  update: 'UPDATE',
  delete: 'DELETE',
  view: 'VIEW',
  viewAll: 'VIEW_ALL',
  review: 'REVIEW',
  assign: 'ASSIGN',
  query: 'QUERY',
  changeRole: 'CHANGE_ROLE',
  pay: 'PAY',
  batchUpdate: 'BATCH_UPDATE',
  createWorkspace: 'CREATE_WORKSPACE',
  updateWorkspace: 'UPDATE_WORKSPACE',
  deleteWorkspace: 'DELETE_WORKSPACE',
  queryUser: 'QUERY_USER',
  companyWide: 'COMPANY_WIDE',
  manageTemplate: 'MANAGE_TEMPLATE',
  invite: 'INVITE',
  viewDetails: 'VIEW_DETAILS',
  close: 'CLOSE',
  updatePermission: 'UPDATE_PERMISSON',
  manageParticipants: 'MANAGE_PARTICIPANTS',
  createAi: 'CREATE_AI',
  updateAi: 'UPDATE_AI',
  deleteAi: 'DELETE_AI',
};
export const sortAlphabeticalOrder = (data = [], sortBy = 'firstName') => {
  if (!Array.isArray(data) || data.length === 0 || !data) return [];

  if (
    sortBy !== 'array' &&
    typeof data[0] === 'object' &&
    !Object.prototype.hasOwnProperty.call(data[0], sortBy)
  ) {
    return data;
  }

  const copyData = data.slice();

  const getValue = (item, key) => {
    if (key === 'array') return item;
    let value = item && item[key];
    if (typeof value === 'string') return value === 'VEHICLE' ? 'FLEET' : value;
    if (typeof value === 'number') return value.toString();
    return '';
  };

  return copyData.sort((a, b) => {
    let valueA, valueB;
    if (sortBy === 'array' && typeof a === 'string' && typeof b === 'string') {
      valueA = a.toLowerCase();
      valueB = b.toLowerCase();
    } else {
      valueA = String(getValue(a, sortBy)).toLowerCase();
      valueB = String(getValue(b, sortBy)).toLowerCase();
    }
    if (valueA < valueB) return -1;
    if (valueA > valueB) return 1;
    return 0;
  });
};

export const flattenUsers = (users = []) => {
  if (!users?.length) {
    return;
  }
  const userMap = new Map();
  users.slice().forEach((u) => userMap.set(u.id, u));
  return Array.from(userMap.values()) || [];
};

export function getPrevSegmentAndId(url) {
  if (!url) return [null, null];
  const parts = url.split('/').filter(Boolean);
  const len = parts.length;
  if (len < 2) return [null, null];
  return [parts[len - 2], parts[len - 1]];
}

export const robotPictures = {
  gear: 'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_gear.jpg',
  announcement:
    'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_announcement.jpg',
  laptop:
    'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_laptop.jpg',
  teach:
    'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_teach.jpg',
  ok: 'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_ok.jpg',
  read: 'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_read.jpg',
  lean: 'https://opas-public.s3.us-east-2.amazonaws.com/graphics/robot/robot_lean.jpg',
};

export function wait(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
